import DirectApi from '../../../directApi';
import {KnownMatches} from './clientMatch';
import {contactKey, invoiceKey, SelectedInvoices} from './invoiceSelection';

/*
 * Create in the import modal: sends the ticked invoices whose Client match is Matched to the xero
 * service, 20 at a time and one batch after another, and gathers one outcome per invoice. Every
 * function is pure except the fetch.
 * See docs/spec/invoicing/055-xero-import-create-matched-invoices.md.
 */

/** Invoices per request: the xero service creates at most 20, one after another. */
export const MAX_INVOICES_PER_REQUEST = 20;

export type ImportOutcome =
	| 'created'
	| 'alreadyImported'
	| 'notImportable'
	| 'needsClient'
	| 'clientChanged'
	| 'totalsMismatch'
	| 'failed'
	| 'notAttempted';

export type ImportResult = {
	tenantId: string;
	invoiceId: string;
	outcome: ImportOutcome;
	forecastInvoiceId?: number;
	companyInvoiceId?: number;
	clientId?: number;
};

export type ImportSummary = {results: ImportResult[]; linkedContacts: number};

/** One invoice as Create sends it: the client is the one the Client match column showed. */
export type ImportRow = {tenantId: string; invoiceId: string; contactId: string; clientId: number};

const OUTCOMES: ReadonlySet<string> = new Set([
	'created',
	'alreadyImported',
	'notImportable',
	'needsClient',
	'clientChanged',
	'totalsMismatch',
	'failed',
	'notAttempted',
]);

/**
 * The ticked rows Create sends (D1): those whose effective match is Matched and whose contact's own
 * answer names the client. New client and review rows are left to US-09 and US-06.
 */
export const rowsToImport = (selection: SelectedInvoices, known: KnownMatches): ImportRow[] => {
	const rows: ImportRow[] = [];
	selection.forEach(row => {
		const answer = known.get(contactKey(row));
		if (row.clientMatch === 'matched' && answer?.match === 'matched') {
			rows.push({tenantId: row.tenantId, invoiceId: row.invoiceId, contactId: row.contactId, clientId: answer.clientId});
		}
	});
	return rows;
};

/**
 * Batches of at most `size` (D2), sorted by organisation and contact. A contact's invoices stay in
 * one batch when they fit, so its client is linked in one request; a contact with more is split.
 */
export const toBatches = (rows: readonly ImportRow[], size = MAX_INVOICES_PER_REQUEST): ImportRow[][] => {
	const groups = new Map<string, ImportRow[]>();
	[...rows]
		.sort((a, b) => contactKey(a).localeCompare(contactKey(b)) || a.invoiceId.localeCompare(b.invoiceId))
		.forEach(row => groups.set(contactKey(row), [...(groups.get(contactKey(row)) ?? []), row]));

	const batches: ImportRow[][] = [];
	let current: ImportRow[] = [];
	groups.forEach(group => {
		if (current.length > 0 && current.length + group.length > size) {
			batches.push(current);
			current = [];
		}
		group.forEach(row => {
			if (current.length === size) {
				batches.push(current);
				current = [];
			}
			current.push(row);
		});
	});
	if (current.length > 0) {
		batches.push(current);
	}
	return batches;
};

/** Every invoice of these rows failed: a request that answered nothing usable. */
export const failedSummary = (batch: readonly ImportRow[]): ImportSummary => ({
	results: batch.map(row => ({tenantId: row.tenantId, invoiceId: row.invoiceId, outcome: 'failed'})),
	linkedContacts: 0,
});

const isObject = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object';
const optionalNumber = (value: unknown): number | undefined => (typeof value === 'number' ? value : undefined);

/** One result per sent invoice, in batch order; an invoice the answer leaves out (or garbles) is failed. */
const parseSummary = (body: unknown, batch: readonly ImportRow[]): ImportSummary | null => {
	if (!isObject(body) || !Array.isArray(body.results)) {
		return null;
	}
	const answered = new Map<string, ImportResult>();
	body.results.forEach(entry => {
		if (
			isObject(entry) &&
			typeof entry.tenantId === 'string' &&
			typeof entry.invoiceId === 'string' &&
			OUTCOMES.has(String(entry.outcome))
		) {
			const result: ImportResult = {
				tenantId: entry.tenantId,
				invoiceId: entry.invoiceId,
				outcome: entry.outcome as ImportOutcome,
			};
			const forecastInvoiceId = optionalNumber(entry.forecastInvoiceId);
			const companyInvoiceId = optionalNumber(entry.companyInvoiceId);
			const clientId = optionalNumber(entry.clientId);
			if (forecastInvoiceId !== undefined) result.forecastInvoiceId = forecastInvoiceId;
			if (companyInvoiceId !== undefined) result.companyInvoiceId = companyInvoiceId;
			if (clientId !== undefined) result.clientId = clientId;
			answered.set(invoiceKey(result), result);
		}
	});
	return {
		results: batch.map(
			row => answered.get(invoiceKey(row)) ?? {tenantId: row.tenantId, invoiceId: row.invoiceId, outcome: 'failed'}
		),
		linkedContacts: typeof body.linkedContacts === 'number' ? body.linkedContacts : 0,
	};
};

/** One batch's outcomes. Never rejects: a request that fails makes every invoice of the batch failed. */
export const postImport = async (batch: ImportRow[]): Promise<ImportSummary> => {
	try {
		const result: Response = await DirectApi.Fetch_Raw_With_Init('xero/importInvoices', {
			method: 'POST',
			headers: {'Content-Type': 'application/json'},
			credentials: 'include',
			body: JSON.stringify({invoices: batch}),
		});
		if (result.status !== 200) {
			return failedSummary(batch);
		}
		return parseSummary(await result.json(), batch) ?? failedSummary(batch);
	} catch {
		return failedSummary(batch);
	}
};

type RunOptions = {
	post?: (batch: ImportRow[]) => Promise<ImportSummary>;
	/** After each batch, with the results so far. */
	onBatch?: (summary: ImportSummary) => void;
};

/**
 * Every row, batch after batch. An invoice answered notAttempted (the Lambda ran short of time) is
 * sent once more in a later batch; each invoice's last answer is kept.
 */
export const runImport = async (rows: ImportRow[], {post = postImport, onBatch}: RunOptions = {}): Promise<ImportSummary> => {
	const results = new Map<string, ImportResult>();
	const order: string[] = [];
	const retried = new Set<string>();
	let linkedContacts = 0;
	let pending = toBatches(rows);

	const summary = (): ImportSummary => ({results: order.map(key => results.get(key) as ImportResult), linkedContacts});

	while (pending.length > 0) {
		const [batch, ...rest] = pending;
		const answer = await post(batch);
		linkedContacts += answer.linkedContacts;
		const again: ImportRow[] = [];
		const byKey = new Map(answer.results.map(entry => [invoiceKey(entry), entry]));
		batch.forEach(row => {
			const key = invoiceKey(row);
			// By key only: a position could belong to another invoice in a short or reordered answer.
			const result: ImportResult = byKey.get(key) ?? {
				tenantId: row.tenantId,
				invoiceId: row.invoiceId,
				outcome: 'failed',
			};
			if (!results.has(key)) {
				order.push(key);
			}
			results.set(key, result);
			if (result.outcome === 'notAttempted' && !retried.has(key)) {
				retried.add(key);
				again.push(row);
			}
		});
		pending = again.length > 0 ? toBatches([...again, ...rest.flat()]) : rest;
		onBatch?.(summary());
	}
	return summary();
};

const isDone = (result: ImportResult): boolean => result.outcome === 'created' || result.outcome === 'alreadyImported';

/** Every invoice sent is in Forecast now (D5): the modal can close. */
export const allDone = (summary: ImportSummary): boolean => summary.results.every(isDone);

/** How many invoices sent are not in Forecast, for the failure line (D5). */
export const notCreatedCount = (summary: ImportSummary): number => summary.results.filter(result => !isDone(result)).length;
